import React, { useState } from "react";
import {
  CaretDown,
  CaretUp,
  Check,
  Info,
  Trash,
  WarningCircle,
} from "@phosphor-icons/react";
import { Label } from "../../../components/ui/Input";
import { GermanDateInput, isoToGerman } from "../../../components/GermanDateInput";
import {
  formatYMD,
  diffYMD,
  effectiveDurationYMD30360,
  monthsBetween30360,
  isAngestelltPosition,
  isPraktikumPosition,
  hasUnresolvedPraktikum,
  hasUnresolvedQualityWarning,
  needsEquivalenceReview,
  setPraktikumTyp,
  stripPraktikumTypPrefix,
  classifyFactorProvenance,
  applyKvFactorRule,
  getEquivalenceTerms,
  isDeterministicSvStatus,
} from "../shared";
import KonsolidiertBadge from "./KonsolidiertBadge";
import FaktorProvenienzBadge from "./FaktorProvenienzBadge";
import CapBypassHints from "./CapBypassHints";
import QualityBadges from "./QualityBadges";
import { isFeatureEnabled } from "../../../config/features";

/** Empty manually-added row, with sensible defaults.
 *
 * Iter 19.64.10 — Setzt `_user_added: true`. Dieser Flag wird über
 * `classifyFactorProvenance` ausgewertet, damit selbst angelegte Zeilen
 * NICHT fälschlich als „Annahme" (LLM-Default) klassifiziert werden,
 * sondern als „Manuell" (= Sachbearbeiter-Erfassung).
 */
export function EmptyRow() {
  return {
    datum_von: "",
    datum_bis: "",
    arbeitgeber: "",
    taetigkeit: "",
    anrechenbar: true,
    anrechnungsfaktor: 1.0,
    anrechenbarkeit_begruendung: "",
    // Iter 19.63.101 — Cap-Bypass-Default. Sichtbar wird das Feld in der
    // UI erst, wenn die KV-Regel `cap_bypass_rules.same_employer` greift
    // UND der Cap durch andere Items bereits erreicht/überschritten ist.
    cap_bypass: false,
    // Iter 20.36.f2 — WT-Kanzlei-Kombiregel-Default. Wird durch eine
    // eigene Checkbox „In WT-Kanzlei erbracht" (Step 3) getoggelt.
    // Sichtbar nur, wenn der KV die Sonderregel `wt_kanzlei_kombiregel`
    // definiert hat.
    wt_kanzlei: false,
    // Iter 19.64.10 — Provenienz-Flag: vom Sachbearbeiter manuell
    // angelegt (nicht vom LLM extrahiert).
    _user_added: true,
  };
}

/**
 * One editable row inside the Step-3 Beschäftigungszeiten table.
 *
 * Renders three display states for the mini "anrechenbar / gesamt" label:
 *   • Full credit  → monthsEffective ≈ months (within 1 day rounding)
 *   • Partial      → 0 < monthsEffective < months (factor < 1 OR overlap-reduced OR cap-reduced)
 *   • Zero         → !anrechenbar OR monthsEffective ≈ 0 (fully overlapped / cap-exhausted)
 *
 * The dates are decomposed using calendar-correct arithmetic
 * (`diffYMD` / `effectiveDurationYMD` from shared.js) to match Austrian
 * labor-law convention.
 */
function VordienstRow({ row, onChange: _rawOnChange, onRemove: _rawOnRemove, index, stichtag, requiresEquivalenceCheck = false, equivalenceTerminology = "gleichwertigkeit", faktorRegeln = {}, praktikumRegeln = {}, capBypassRules = null, isCapReached = false, capBypassSonderregeln = [], mode = "detail", selected = false, onSelectToggle = null }) {
  // Iter 20.36.f14.24 — `open`-State für Begründungs-Toggle entfernt.
  // Die zugehörige Chevron-Button im Detail-Bereich wurde entfernt, weil
  // sie mit dem Chevron in der kompakten Row-Header-Zeile redundant war.
  // Die Begründung ist ab jetzt immer sichtbar, sobald die Zeile
  // aufgeklappt ist — also genau dann, wenn dieser Detail-Block rendert.
  // Mockup→Live Phase 2 — Kompakt-Zeile + Detail-Drawer.
  // Wenn mode="compact": Zeile rendert nur den Kompakt-Header (~40 px);
  // Klick auf Chevron → rowExpanded=true → volle Detail-Ansicht klappt als
  // Drawer auf. Bei mode="detail" (Default) unverändertes Verhalten.
  const isCompact = mode === "compact";
  // Manuell hinzugefügte Zeilen (noch kein Von-Datum) starten aufgeklappt,
  // damit der User sofort tippen kann. Bereits gefüllte Zeilen (aus AI-
  // Extraktion oder nach dem ersten Roundtrip) starten kompakt/geschlossen.
  const [rowExpanded, setRowExpanded] = useState(() => !row.datum_von);
  // Iter 20.36.f14.22 — „Geprüft"-Toggle jetzt als Row-Feld statt lokalem
  // useState. Vorteile: (a) `rowNeedsAttention` in shared.js kann den
  // Zustand lesen → Filter-Chip „Prüfen"-Count sinkt bei Verifikation;
  // (b) Left-Streifen kann Backend-korrekt hidden werden; (c) State
  // überlebt Recalc-Roundtrips (via `mapBackendItem`-Fallback), aber
  // reset bei Re-Extract (Extract-Path ruft `mapBackendItem` ohne
  // Fallback auf, siehe StepThree.jsx:441).
  const reviewVerified = row._review_verified === true;
  // Iter 19.68.3 — Quality-Warning Status + onChange-Wrapper.
  //
  // Wenn das Backend eine `_quality_flags`-Soft-Warning gesetzt hat und der
  // User die Row noch nicht bestätigt hat, wird JEDE User-Edit-Aktion (egal
  // welches Feld) implizit als „User hat sich das angesehen" gewertet → wir
  // setzen `_quality_ack: true` mit. Das ist auch der Mechanismus, mit dem
  // der orange Border-Stripe + die Hinweisbox-Zählung verschwinden, sobald
  // der User die Position editiert.
  //
  // Wrap-Strategie: Die Prop wird in der Destructure als `_rawOnChange`
  // umbenannt, und wir definieren lokal `onChange` (Block-scoped), das im
  // gesamten Komponenten-Body benutzt wird. Dadurch müssen die ~12 bestehen-
  // den `onChange(...)`-Aufrufe NICHT einzeln angepasst werden.
  const qualityUnresolved = hasUnresolvedQualityWarning(row);
  const onChange = (next) => {
    if (qualityUnresolved) {
      _rawOnChange(index, { ...next, _quality_ack: true });
    } else {
      _rawOnChange(index, next);
    }
  };
  // Audit-Fix P2 — Parent liefert index-basierte, stabile Handler; die
  // internen Callsites (`onRemove` als onClick) bleiben unverändert.
  const onRemove = () => _rawOnRemove(index);
  // Iter 19.63.74 — Praktikum-Anstellungstyp-Status für UI-Gating.
  const isPraktikum = isPraktikumPosition(row.taetigkeit);
  const praktikumUnresolved = hasUnresolvedPraktikum(row);
  const praktikumTyp = row.praktikum_anstellungstyp;
  // Iter 19.63.80 — Faktor-Provenienz für Pill-Badge (deterministisch).
  // Iter 19.74 (Baustein C) — CV-Uncertainty-Downgrade an classifyFactorProvenance
  // durchreichen. Bei aus Lebenslauf abgeleiteten Zeilen mit Gleichwertigkeits-
  // Prüfung wird der Badge ANNAHME statt KV-REGEL.
  const factorProvenance = classifyFactorProvenance(row, faktorRegeln, {
    equivalenceCheckRequired: requiresEquivalenceCheck === true,
  });
  // Iter 19.63.81 — Annahme-Indikator: Row wird subtil slate-getönt, wenn
  // der Faktor 1,0 nur „angenommen" wurde (kein KV-Regel-Match, kein
  // Praktikum, kein manueller Override). Vermittelt dem Sachbearbeiter
  // visuell, dass hier eine Prüfung sinnvoll wäre — ohne aufdringlich
  // wie eine echte Warnung zu wirken (deshalb slate statt amber).
  const isAnnahme = factorProvenance === "default";
  // Iter 19.63.101 — Cap-Bypass „Selber Arbeitgeber".
  //
  // Sichtbarkeit der Checkbox: Nur wenn der KV diese Sonderregel hat
  // (cap_bypass_rules.same_employer != null), die Position überhaupt
  // anrechenbar ist, UND der Cap durch andere Items bereits erreicht ist
  // (isCapReached). Bei nicht-erreichtem Cap macht der Bypass keinen
  // Unterschied — wir blenden ihn aus, um die UI ruhig zu halten.
  //
  // Wenn bereits gesetzt, bleibt die Checkbox sichtbar (auch wenn der Cap
  // gerade nicht mehr erreicht ist) — sonst könnte der User seine Wahl
  // nicht mehr rückgängig machen.
  const hasSameEmployerBypass = Boolean(capBypassRules?.same_employer);
  // Iter 19.66.8 — Selber-Arbeitgeber-Sonderregel gilt nur für unselbst-
  // ständige Beschäftigungsverhältnisse. Selbstständige, Werkverträge,
  // freie Dienstnehmer und §-Versicherungsverhältnisse (GSVG/BSVG/FSVG)
  // sind kollektivvertraglich KEIN Dienstnehmer-Vordienst → Checkbox
  // ausblenden. Pattern wird sowohl gegen die Tätigkeit als auch gegen
  // die Begründung geprüft (LLM hinterlegt den Selbstständigkeits-Hinweis
  // oft erst in der Begründung).
  const _SELBSTSTAENDIG_RE = /(\bselbst(?:st)?[aä]ndig|\bgewerblich|\bfreie?r?\s+dienstnehmer|\bwerkvertrag|\b(?:gsvg|bsvg|fsvg)\b)/i;
  const isSelbststaendigPosition =
    _SELBSTSTAENDIG_RE.test(row.taetigkeit || "") ||
    _SELBSTSTAENDIG_RE.test(row.anrechenbarkeit_begruendung || "");
  const showBypassCheckbox =
    hasSameEmployerBypass &&
    row.anrechenbar &&
    !isSelbststaendigPosition &&
    (isCapReached || Boolean(row.cap_bypass));
  const bypassActive = Boolean(row._cap_bypassed);
  const bypassChainWarning =
    typeof row._bypass_chain_warning === "string" && row._bypass_chain_warning.length > 0
      ? row._bypass_chain_warning
      : null;
  // Iter 20.36.f14.19 — Konsolidierte „Zeile braucht Prüfung"-Erkennung.
  // Deckt sich mit den Checks im kompakten Info-Badge (`checks[]` weiter
  // unten). Wird für das linke amber Akzent-Streifen genutzt: Zeile hat
  // mindestens einen offenen Prüfpunkt → 3-px-Streifen am linken Rand.
  // Bewusst OHNE `praktikumUnresolved` und `bypassActive` — diese haben
  // bereits ihre eigenen visuellen Marker (kräftigeres Amber-Panel bzw.
  // eigener 4-px-Balken), das würde sich sonst doppeln.
  const hasReviewSignal = (
    qualityUnresolved
    || needsEquivalenceReview(row, requiresEquivalenceCheck)
    || Boolean(bypassChainWarning)
    || row._praesenzdienst_needs_review === true
    || row.was_laufend === true
  );
  // Iter 20.36.f2 — WT-Kanzlei-Kombiregel: pro Zeile eine Zusatz-Checkbox
  // „In WT-Kanzlei erbracht". Sichtbar nur, wenn der KV die Sonderregel
  // `wt_kanzlei_kombiregel` (aktiv) definiert hat UND die Zeile anrechenbar
  // ist. Wenn der Sachbearbeiter das Häkchen später wieder entfernt, muss
  // die Checkbox weiter sichtbar bleiben (analog zu Cap-Bypass), damit ein
  // Untoggle immer möglich ist.
  const wtKanzleiRule = Array.isArray(capBypassSonderregeln)
    ? capBypassSonderregeln.find(
        (r) => r && r.typ === "wt_kanzlei_kombiregel" && r.aktiv !== false,
      )
    : null;
  // Iter 20.36.f7 — SV-Status-Zeilen (Wochengeld, Karenz, Mutterschutz,
  // Präsenzdienst, AMS-Bezug etc.) sind zwar anrechenbar, aber nicht
  // facheinschlägig → WT-Kanzlei-Regel gilt für sie nicht. Checkbox
  // ausblenden, sonst wirkt sie missverständlich („Warum kann ich für
  // Wochengeldbezug eine WT-Kanzlei-Zuordnung wählen?").
  const isSvStatusRow = isDeterministicSvStatus(
    row.taetigkeit || "",
    row.anrechenbarkeit_begruendung || "",
  );
  const showWtKanzleiCheckbox = Boolean(
    wtKanzleiRule && !isSvStatusRow && (row.anrechenbar || row.wt_kanzlei),
  );
  const wtKanzleiActive = Boolean(row.wt_kanzlei);
  // Iter 19.63.73 — Strikt 30/360-konforme Roh-Monate (Y*12 + M + D/30),
  // matcht Backend `_compute_row_dauer`. Vorheriges `monthsBetween` rechnete
  // kalendarisch (days/30.4375) und produzierte beim Faktor-Roundtrip einen
  // ±1-Tag-Drift gegenüber dem Backend.
  const months = monthsBetween30360(row.datum_von, row.datum_bis, stichtag);
  // Effective months that count toward the cap. Use the server-computed
  // months_anrechenbar when present (so cap-applied items stay consistent),
  // otherwise compute on the fly: months × anrechnungsfaktor.
  const faktor = typeof row.anrechnungsfaktor === "number" ? row.anrechnungsfaktor : 1.0;
  // Iter 19.31 FIX: When the local factor disagrees with a previously cached
  // months_anrechenbar (e.g. from an earlier KI-Review or auto-rule), the
  // factor wins. This prevents the row from showing the old duration after
  // a manual factor change or rule re-application.
  const expectedFromFactor = row.anrechenbar ? months * faktor : 0;
  let monthsEffective;
  if (typeof row.months_anrechenbar === "number") {
    // Only trust the cached value if it's plausibly consistent with the
    // current factor (allow it to be LOWER than months × faktor due to
    // cap/overlap reductions, but never HIGHER — the factor wins).
    monthsEffective = Math.min(row.months_anrechenbar, expectedFromFactor);
  } else {
    monthsEffective = expectedFromFactor;
  }
  // Iter 19.63.26 — Display-Fix für 1-Tag-Items: monthsEffective einer
  // 1-Tag-Zeile = 1/30.4375 ≈ 0.0329 < 0.034 → fälschlich als isZero
  // markiert ("0 T." durchgestrichen). Backend-authoritative Werte
  // dauer_anrechenbar_* überschreiben diese ungenaue Schwelle: wenn das
  // Backend echte 30/360-Tage geliefert hat, nutzen wir die direkt zur
  // isZero-Entscheidung (strikt 0). Manuelle Frisch-Zeilen ohne
  // Backend-Roundtrip behalten die 0.034-Schwelle als Fallback.
  // Math-Logik bleibt UNVERÄNDERT — das ist rein eine Display-Korrektur.
  let isZero;
  const hasBackendDays = (
    typeof row.dauer_anrechenbar_jahre === "number" ||
    typeof row.dauer_anrechenbar_monate === "number" ||
    typeof row.dauer_anrechenbar_tage === "number"
  );
  if (!row.anrechenbar) {
    isZero = true;
  } else if (hasBackendDays) {
    const backendDays =
      (row.dauer_anrechenbar_jahre || 0) * 360 +
      (row.dauer_anrechenbar_monate || 0) * 30 +
      (row.dauer_anrechenbar_tage || 0);
    isZero = backendDays < 0.001;
  } else {
    isZero = monthsEffective <= 0.034;
  }
  let isPartial;
  // Iter 19.63.107 — Saubere SSOT-basierte Partial-Klassifikation.
  // Vorher: reiner Float-Vergleich `monthsEffective < months - 0.034`. Das
  // produzierte false-positive Partial-Indikatoren bei 30/360-Rundungsdrift
  // an Monatsenden (29./30./31.) — Backend und Frontend lieferten minimal
  // verschiedene Float-Werte für dieselbe YMD-Anzeige (z.B. Item
  // 29.09.2016 → 18.09.2017: beide Werte = „11 M. 21 T.", aber Backend
  // months_anrechenbar = 11.63 vs. Frontend monthsBetween30360 = 11.70).
  // Resultat: orange Markierung + „gesamt"-Sublabel, obwohl visuell IDENTISCH.
  //
  // Neue Logik: Wenn das Backend explizite Display-Strings für Rohdauer UND
  // Anrechenbarer Dauer liefert, ist die Reduktions-Frage durch String-
  // Identität exakt entschieden — `display_raw === display_anrechenbar`
  // bedeutet: keine sichtbare Reduktion → kein Partial-Indikator.
  // Das ist die korrekte SSOT, weil die UI exakt diese Strings anzeigt.
  // Bei manuell editierten Frisch-Zeilen ohne Backend-Roundtrip fällt die
  // Logik auf den alten Float-Vergleich zurück.
  if (
    typeof row.display_raw === "string" &&
    typeof row.display_anrechenbar === "string"
  ) {
    isPartial = !isZero && row.display_raw.trim() !== row.display_anrechenbar.trim();
  } else {
    isPartial = !isZero && monthsEffective < months - 0.034;
  }
  const showEff = isPartial || isZero;
  // Iter 19.63.22 / 19.63.24 — Format stichtag + Stichtag-1 für die
  // persistenten Hinweise. Stichtag-1 = letzter Tag der laufenden
  // Vorbeschäftigung (Stichtag selbst = erster Tag im neuen Job).
  const stichtagLabel = (() => {
    if (!stichtag) return "heute";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(stichtag).trim());
    return m ? `${m[3]}.${m[2]}.${m[1]}` : "heute";
  })();
  const stichtagMinusOneLabel = (() => {
    if (!stichtag) return "gestern";
    try {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(stichtag).trim());
      if (!m) return "1 Tag vor Stichtag";
      const d = new Date(`${stichtag}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() - 1);
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const yyyy = d.getUTCFullYear();
      return `${dd}.${mm}.${yyyy}`;
    } catch (_e) {
      return "1 Tag vor Stichtag";
    }
  })();
  return (
    <li
      data-testid={`vd-row-${index}`}
      data-needs-equivalence-review={
        needsEquivalenceReview(row, requiresEquivalenceCheck) ? "true" : undefined
      }
      data-needs-praktikum-review={
        praktikumUnresolved ? "true" : undefined
      }
      data-needs-quality-review={
        qualityUnresolved ? "true" : undefined
      }
      data-faktor-provenance={factorProvenance}
      data-bypass-active={bypassActive ? "true" : undefined}
      className={[
        "border rounded-sm relative",
        // Iter 19.63.22 — Bei „laufend" markierte Zeile als „Achtung" hervorheben:
        // gelbe Border + leicht gelber Hintergrund, statt grauer Default-Border.
        // Iter 19.63.63 — Bei KVs mit Gleichwertigkeitsprüfung: anrechenbare
        // Angestelltenpositionen leicht warm-getönt (User muss diese manuell
        // auf fachliche Gleichwertigkeit zur neuen Rolle prüfen). Hat
        // Vorrang über die normale Border, aber „laufend" sticht weiter heraus.
        // Iter 19.73 (Bug 1) — Der Marker greift jetzt auch bei ANNAHME-
        // Zeilen (ambivalente Tätigkeit oder Backend-Downgrade), damit
        // Sachbearbeiter die Gleichwertigkeit gerade in den unsichersten
        // Fällen sieht.
        // Iter 19.63.68 / 19.63.74 — Praktikum-Zeilen leicht warm-getönt;
        // bei noch unentschiedenem Anstellungstyp deutlicher hervorheben
        // (kräftigeres Amber + leicht stärkerer Hintergrund + 4px-Akzent-
        // Balken am linken Rand, siehe Iter 19.63.76).
        // Iter 19.63.81 — Annahme-Indikator (niedrigste Priorität, slate-Ton):
        // greift nur wenn keine echte Warnung (amber) anliegt. Slate-Tönung
        // signalisiert „bitte prüfen, Faktor angenommen", ohne als kritische
        // Warnung wahrgenommen zu werden.
        // Iter 19.63.101 — Bypass-aktive Zeile (User-Wahl „Selber Arbeitgeber"
        // + Backend hat tatsächlich bypassed): kräftigeres Amber, damit der
        // User auf einen Blick sieht, dass diese Position außerhalb der
        // KV-Höchstanrechnungsdauer zählt. Schlägt „laufend"-Border-Stil,
        // weil der Bypass-Mechanismus für die Gesamt-Anrechnung zentral ist.
        // Mockup→Live Phase 4 — „ruhige Semantik": Bei aktivem
        // step3NewTerms-Flag werden die BEIDEN schwachen Amber-Fälle
        // (needsEquivalenceReview + Praktikum-anrechenbar-ohne-offene-
        // Entscheidung) auf neutral zurückgestellt. Sie sind bereits
        // durch Badges/Chips visuell kommuniziert; das Zeilen-Panel
        // muss nicht mit-warnen. Aktions-fordernde States (bypassActive,
        // was_laufend, praktikumUnresolved) behalten ihre Amber-Optik.
        // Iter 20.36.f14.20 — „laufend"-Sondertreatment (border-amber-400
        // bg-amber-50/40) entfernt. User-Feedback: die gelbe Umrandung +
        // Hintergrund war zu prominent — der 3-px amber Left-Streifen
        // (siehe unten `hasReviewSignal`) + der orange „laufend"-Text im
        // Datumsfeld sind bereits eindeutige Signale. Konsistenz mit allen
        // anderen prüfrelevanten Zeilen.
        bypassActive
          ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300/40"
          : praktikumUnresolved
            ? "border-amber-400 bg-amber-50/60"
            : (!isFeatureEnabled("step3NewTerms") && needsEquivalenceReview(row, requiresEquivalenceCheck))
              ? "border-amber-200 bg-amber-50/30"
              : (!isFeatureEnabled("step3NewTerms") && row.anrechenbar && isPraktikum)
                ? "border-amber-200 bg-amber-50/30"
                : isAnnahme
                  ? "border-slate-300 bg-slate-50/40"
                  : "border-ink-200 bg-white",
      ].join(" ")}
      id={`vd-row-anchor-${index}`}
    >
      {/* Iter 19.63.76 — Vertikaler Akzent-Balken am linken Rand für
          unentschiedene Praktikum-Zeilen. Beim Scrollen sofort als
          „braucht Aufmerksamkeit" erkennbar. Greift NUR bei
          `praktikumUnresolved`, sonst kein Balken. */}
      {praktikumUnresolved && (
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500 rounded-l-sm"
          data-testid={`vd-row-${index}-praktikum-accent-bar`}
        />
      )}
      {/* Iter 20.36.f14.19 — Dünner amber Left-Border-Streifen für
          Prüfrelevante Zeilen. Sichtbar-Signal für den Sachbearbeiter:
          „hier ist etwas offen, ohne dass das Panel dominant gelb wird".
          Iter 20.36.f14.20 — „was_laufend"-Ausschluss entfernt.
          Iter 20.36.f14.22 — Zusätzlicher Ausschluss bei User-Verifikation
          (`_review_verified=true`): wenn der Sachbearbeiter die Zeile
          abgehakt hat, verschwindet der Streifen — visuell wirkt die
          Zeile dann wie eine normale, unauffällige Position. */}
      {hasReviewSignal && !praktikumUnresolved && !bypassActive && !reviewVerified && (
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-amber-400/80 rounded-l-sm"
          data-testid={`vd-row-${index}-review-accent-bar`}
        />
      )}
      {/* Iter 19.68.3 — Vertikaler Akzent-Balken für ungeprüfte
          Quality-Warnings (Hallucination-Verdacht / Folgezeilen-Übernahme).
          Sichtbarer (4 px) und kräftiger orange als Standard-Badge — soll
          beim Scrollen NICHT übersehen werden. Verschwindet sofort, sobald
          der User die Position manuell bearbeitet ODER die „Geprüft"-
          Checkbox in der QualityBadges-Detailbox aktiviert. Greift NICHT
          gleichzeitig mit dem Praktikum-Balken (Praktikum hat Vorrang —
          Praktikum-Anstellungstyp ist eine separate Pflicht-Entscheidung). */}
      {qualityUnresolved && !praktikumUnresolved && (
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500 rounded-l-sm"
          data-testid={`vd-row-${index}-quality-accent-bar`}
        />
      )}
      {/* ==================================================================
          Mockup→Live Phase 2 — KOMPAKT-HEADER (~40 px, immer sichtbar in
          Kompakt-Modus). Enthält die wichtigsten Info-Felder + Faktor + ANR
          + Chevron. Klick auf Chevron öffnet die Detail-Sektion (unten)
          als „Drawer". Editier-Aktionen (Faktor, ANR) sind auch im
          Kompakt-Modus direkt möglich, Datum / Arbeitgeber / Tätigkeit
          erfordern das Öffnen der Detail-Ansicht.
          ================================================================== */}
      {isCompact && (
        <div
          className={[
            "flex items-center gap-2 px-3 py-2 min-h-[44px] cursor-pointer",
            selected ? "bg-brand-50/70" : "",
          ].join(" ")}
          data-testid={`vd-row-${index}-compact-header`}
          role="button"
          tabIndex={0}
          aria-expanded={rowExpanded}
          aria-label={rowExpanded ? "Details schließen" : "Details öffnen"}
          onClick={() => setRowExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setRowExpanded((v) => !v);
            }
          }}
        >
          {/* Mockup→Live Phase 5 — Multi-Select-Checkbox (Kompakt-Modus).
              Nur sichtbar wenn StepThree einen onSelectToggle-Callback
              übergibt (= Bulk-Actions-Flag aktiv). Bei angehakter Zeile
              wird der Header dezent brand-tinted. */}
          {onSelectToggle && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onSelectToggle(index)}
              className="h-4 w-4 shrink-0 accent-brand-500 cursor-pointer"
              data-testid={`vd-row-${index}-compact-select`}
              aria-label={selected ? "Auswahl entfernen" : "Zeile auswählen"}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {/* Datum-Range (read-only, kompakt) */}
          <span
            className="w-[186px] shrink-0 font-mono text-[12px] text-ink-800 tabular-nums whitespace-nowrap"
            data-testid={`vd-row-${index}-compact-daterange`}
            title={row.was_laufend ? `laufend (bis ${stichtagMinusOneLabel})` : ""}
          >
            {isoToGerman(row.datum_von) || <span className="text-warm-400">TT.MM.JJJJ</span>}
            <span className="text-warm-500"> – </span>
            {row.was_laufend
              ? <span className="text-amber-700 font-semibold">laufend</span>
              : (isoToGerman(row.datum_bis) || <span className="text-warm-400">TT.MM.JJJJ</span>)}
          </span>
          {/* Dauer (anrechenbar + brutto) */}
          {Boolean(row.datum_von && (row.datum_bis || row.was_laufend)) && (() => {
            const hasBackendTotal = typeof row.display_raw === "string";
            const hasBackendEff = typeof row.display_anrechenbar === "string";
            const totalLabel = hasBackendTotal
              ? row.display_raw
              : formatYMD(diffYMD(row.datum_von, row.datum_bis, stichtag));
            let effLabel;
            if (hasBackendEff) {
              effLabel = row.display_anrechenbar;
            } else if (isZero) {
              effLabel = "0 T.";
            } else if (isPartial) {
              effLabel = formatYMD(effectiveDurationYMD30360(monthsEffective));
            } else {
              effLabel = totalLabel;
            }
            return (
              <span
                className="w-[104px] shrink-0 leading-tight text-left"
                data-testid={`vd-row-${index}-compact-duration`}
              >
                <span className={[
                  "block text-[12px] font-semibold tabular-nums",
                  isZero
                    ? "text-warm-500 line-through"
                    : (isPartial ? "text-brand-600" : "text-ink-900"),
                ].join(" ")}>
                  {isZero ? "0 T." : effLabel}
                </span>
                <span className="block text-[10px] text-warm-500 tabular-nums">
                  brutto {totalLabel}
                </span>
              </span>
            );
          })()}
          {/* Employer + Tätigkeit (truncated) */}
          <span
            className="min-w-0 flex-1 truncate text-[12px] text-ink-700"
            data-testid={`vd-row-${index}-compact-desc`}
            title={`${row.taetigkeit || "(keine Tätigkeit)"} – ${row.arbeitgeber || "(kein Arbeitgeber)"}`}
          >
            <span className="font-medium text-ink-900">{row.taetigkeit || <em className="text-warm-500">Tätigkeit fehlt</em>}</span>
            <span className="text-ink-500"> — {row.arbeitgeber || <em className="text-warm-500">Arbeitgeber fehlt</em>}</span>
          </span>
          {/* Faktor % — inline editierbar (analog Detail) */}
          {row.anrechenbar ? (
            <div
              className={[
                "flex shrink-0 items-center gap-0.5 rounded-sm border px-1 py-0.5",
                row._factor_manual === true
                  ? "border-amber-400 bg-amber-50"
                  : "border-ink-200 bg-white",
              ].join(" ")}
              title="Anrechnungsfaktor in % (0–100). Standard 100 %."
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="number"
                min={0}
                max={100}
                step={5}
                value={Math.round(faktor * 100)}
                onChange={(e) => {
                  const raw = e.target.value;
                  const pct = raw === ""
                    ? 0
                    : Math.max(0, Math.min(100, Number(raw) || 0));
                  const newFaktor = pct / 100;
                  onChange({
                    ...row,
                    anrechnungsfaktor: newFaktor,
                    _factor_manual: true,
                    months_anrechenbar: undefined,
                    effective_days_anrechenbar: undefined,
                    effective_period: undefined,
                    dauer_anrechenbar_jahre: undefined,
                    dauer_anrechenbar_monate: undefined,
                    dauer_anrechenbar_tage: undefined,
                    display_raw: undefined,
                    display_anrechenbar: undefined,
                    display_anrechenbar_pre_cap: undefined,
                    dauer_anrechenbar_pre_cap_jahre: undefined,
                    dauer_anrechenbar_pre_cap_monate: undefined,
                    dauer_anrechenbar_pre_cap_tage: undefined,
                    _cap_reduced: undefined,
                    _cap_zeroed: undefined,
                    // Iter 20.36.f9 — Idempotenz-Marker der Sonderregeln
                    // ebenfalls invalidieren. Ohne diesen Reset würde der
                    // Backend-Recalc den manuellen Faktor via
                    // `_revert_altersfaktor`/`_revert_wt` auf den zuvor
                    // gespeicherten `_*_previous_factor` zurückrollen und
                    // die manuelle Eingabe ginge verloren. Danach re-
                    // applied das Backend die aktuellen Regeln (Alters-
                    // faktor, WT-Kanzlei) frisch auf den neuen Manual-
                    // Wert — halbiert also z.B. korrekt zu 0.5, wenn die
                    // 5-J-Ausnahme durch andere Toggles wegfällt.
                    _altersfaktor_applied: false,
                    _altersfaktor_previous_factor: null,
                    _altersfaktor_split: undefined,
                    _wt_non_kanzlei_reduced: false,
                    _wt_previous_factor: null,
                  });
                }}
                className={[
                  "w-12 bg-transparent px-1 py-0.5 text-right text-[13px] font-medium tabular-nums focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400",
                  row._factor_manual === true ? "text-amber-900 font-semibold" : "text-ink-800",
                ].join(" ")}
                data-testid={`vd-row-${index}-compact-faktor-percent`}
                aria-label="Anrechnungsfaktor in Prozent"
              />
              <span className={[
                "text-[11px] font-medium select-none",
                row._factor_manual === true ? "text-amber-700" : "text-ink-500",
              ].join(" ")}>%</span>
            </div>
          ) : (
            <span className="w-14 shrink-0 text-center text-[13px] text-warm-400">—</span>
          )}
          {/* Provenance-Chip: KV-REGEL / ANNAHME / MANUELL / KV-PRAKT. /
              PRAKTIKUM (unentschieden). Iter 20.03.P5-fix — Punkt 2 aus
              User-Report Martina Fall 4: Praktikum-Zeilen ohne Anstellungs-
              typ-Wahl zeigen im Kompakt-Header statt „—" ein amber
              „PRAKTIKUM"-Badge, damit der User beim Scannen sofort sieht,
              wo eine Entscheidung fehlt. Sobald der User Angestellter/
              Arbeiter wählt (via Warn-Dot → Drawer → Selector), flippt
              das Badge automatisch auf „KV-Prakt." (emerald).

              Iter 20.06 — KV-Regel-Chip auf stummes Häkchen reduziert.
              Text-Chip „KV-Regel" wurde in 80–90 % der Zeilen zu visuellem
              Rauschen. Ein grünes Häkchen kommuniziert „alles ok" ohne
              Herkunfts-Aussage (wörtlich im KV vs. abgeleitete Standard-
              regel). Die aufmerksamkeits-fordernden Badges (ANNAHME,
              MANUELL, PRAKTIKUM) treten dadurch von selbst hervor.
              Barrierefreiheit: aria-label + title-Tooltip mit „Nach KV
              geregelt". Die anderen Chips (KV-Prakt., Manuell, Annahme)
              bleiben Text — dort trägt der Text semantische Information,
              die ein Icon nicht ersetzen kann. */}
          {isPraktikum && praktikumUnresolved ? (
            // Iter 20.11 — Inline-Choice-Buttons statt statischem Amber-Chip.
            // Der Sachbearbeiter kann den Praktikum-Anstellungstyp direkt in
            // der Kompakt-Zeile wählen, ohne den Detail-Drawer aufzuklappen.
            // Nach der Wahl flippt der Chip auf emerald „KV-Prakt." (unten).
            <span
              className="shrink-0 inline-flex items-center gap-0.5 rounded-sm border border-amber-300 bg-amber-50/60 p-0.5"
              data-testid={`vd-row-${index}-compact-provenance-chip`}
              data-provenance="praktikum"
              title="Praktikum erkannt — Anstellungs-Typ direkt wählen"
            >
              <span className="sr-only">Praktikum-Typ wählen:</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({
                    ...setPraktikumTyp(row, "angestellter", faktorRegeln, praktikumRegeln),
                    _quality_ack: true,
                  });
                }}
                data-testid={`vd-row-${index}-compact-praktikum-angestellter`}
                className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm text-amber-900 hover:bg-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-crisp"
                title="Als Angestellter einstufen"
              >
                Angest.
              </button>
              <span className="text-amber-400 text-[9px]" aria-hidden="true">/</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({
                    ...setPraktikumTyp(row, "arbeiter", faktorRegeln, praktikumRegeln),
                    _quality_ack: true,
                  });
                }}
                data-testid={`vd-row-${index}-compact-praktikum-arbeiter`}
                className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm text-amber-900 hover:bg-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-crisp"
                title="Als Arbeiter einstufen"
              >
                Arbeit.
              </button>
            </span>
          ) : factorProvenance === "kv-rule" ? (
            <span
              className="shrink-0 inline-flex items-center justify-center rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 h-[22px] w-[26px]"
              data-testid={`vd-row-${index}-compact-provenance-chip`}
              data-provenance="kv-rule"
              role="img"
              aria-label="Nach KV geregelt"
            >
              <Check size={13} weight="bold" aria-hidden="true" />
            </span>
          ) : (
          <span
            className={[
              "shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider",
              factorProvenance === "kv-praktikum"
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : factorProvenance === "manual"
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : factorProvenance === "default"
                    ? "border-slate-300 bg-slate-50 text-slate-700"
                    : "border-ink-200 bg-white text-ink-500",
            ].join(" ")}
            data-testid={`vd-row-${index}-compact-provenance-chip`}
            data-provenance={factorProvenance}
          >
            {factorProvenance === "kv-praktikum" && "Praktikum"}
            {factorProvenance === "manual" && "Manuell"}
            {factorProvenance === "default" && "Annahme"}
            {factorProvenance === "n/a" && "—"}
          </span>
          )}
          {/* Warn-Indikator (Praktikum unresolved, Quality unresolved,
              Gleichwertigkeit prüfen, Bypass-Kettenregel verletzt) —
              zusammengefasst in einem einzelnen Amber-Dot mit Tooltip.
              „laufend" + Konsolidierung werden BEWUSST ausgelassen, weil
              sie bereits visuell woanders erkennbar sind
              (Datum-Zelle bzw. Konsolidiert-Badge im Detail-Drawer) —
              der Warn-Dot bleibt reserved für echte User-Aktions-Prompts. */}
          {(() => {
            // Iter 20.11 — praktikumUnresolved wird nicht mehr als Warn-Dot-
            // Grund geführt, weil die Aktion jetzt direkt inline im Provenance-
            // Slot erledigbar ist (Angest./Arbeit.-Buttons statt statisches
            // Amber-Chip). Ein zusätzlicher Dot für denselben Anlass wäre
            // Doppel-Signalisierung.
            // Iter 20.36.f14.16 — Checks strukturiert mit Kurz-Label + Detail.
            // User-Wunsch: Kategorien sollen im kollabierten Header inline
            // sichtbar sein (nicht erst per Hover). Kurz-Labels sind so
            // gewählt, dass 2 nebeneinander in einem Chip passen (≤ 20
            // Zeichen zusammen); bei ≥ 3 Prüfungen wird die 3. als „+N"
            // zusammengefasst. Tooltip listet weiterhin alle Details.
            const checks = [];
            if (qualityUnresolved) checks.push({ short: "Extraktion", detail: "Extraktions-Warnung ungeprüft" });
            if (needsEquivalenceReview(row, requiresEquivalenceCheck)) {
              const noun = getEquivalenceTerms(equivalenceTerminology).noun;
              checks.push({ short: noun, detail: `${noun} prüfen` });
            }
            if (bypassChainWarning) checks.push({ short: "Lücken-Kette", detail: "Lücken-Kettenregel verletzt" });
            if (row._praesenzdienst_needs_review === true) {
              checks.push({ short: "Präsenzdienst", detail: "Präsenzdienst — Umschließung durch DV prüfen" });
            }
            // Iter 20.36.f14.18 — „Laufend"-Zeile ist ebenfalls prüfrelevant
            // (Enddatum offen → Sachbearbeiter muss verifizieren, ob die
            // Position wirklich noch läuft oder inzwischen beendet ist).
            // Analog zu Präsenzdienst nur als Info-Hinweis im Hover; die
            // amber Row-Border + der „laufend"-Chip im Datum sind bereits
            // eigenständig als visuelle Signale vorhanden.
            if (row.was_laufend === true) {
              checks.push({ short: "Laufend", detail: "Laufend — Enddatum offen, bitte auf Aktualität prüfen" });
            }
            if (checks.length === 0) return null;
            // Iter 20.36.f14.21 — Badge-Zustände:
            //  • !verified & Ruhe   → amber Info-Icon
            //  • !verified & Hover  → amber Check-Icon (Signal „ich kann abhaken")
            //  • verified          → emerald Check-Icon (Häkchen sichtbar)
            //  • Klick             → toggelt verified (kein Row-Expand mehr,
            //    weil der Nutzer explizit gesagt hat: reiner Ab-/Anhak-Toggle)
            const label = reviewVerified
              ? `Zeile geprüft (${checks.length} Punkt${checks.length !== 1 ? "e" : ""}) — Klick zum Zurücksetzen`
              : `${checks.length} Prüfung${checks.length !== 1 ? "en" : ""} offen — Klick zum Abhaken`;
            return (
              <span className="relative inline-flex group shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({ ...row, _review_verified: !reviewVerified });
                  }}
                  className={[
                    "shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full border transition-crisp focus:outline-none focus:ring-1",
                    reviewVerified
                      ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 focus:ring-emerald-500"
                      : "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 focus:ring-amber-500",
                  ].join(" ")}
                  aria-label={label}
                  aria-pressed={reviewVerified}
                  data-testid={`vd-row-${index}-compact-warn-dot`}
                  data-hint-count={checks.length}
                  data-verified={reviewVerified ? "true" : "false"}
                >
                  {reviewVerified ? (
                    <Check size={12} weight="bold" aria-hidden="true" />
                  ) : (
                    <>
                      <Info
                        size={12}
                        weight="fill"
                        aria-hidden="true"
                        className="group-hover:hidden"
                      />
                      <Check
                        size={12}
                        weight="bold"
                        aria-hidden="true"
                        className="hidden group-hover:block"
                      />
                    </>
                  )}
                </button>
                {/* Iter 20.36.f14.17 — Hover-Popover mit Prüf-Liste.
                    Iter 20.36.f14.21 — Titel reflektiert Verified-Zustand
                    (rein sprachlich; keine sonstige Logik). */}
                <div
                  role="tooltip"
                  data-testid={`vd-row-${index}-compact-warn-popover`}
                  className="hidden group-hover:block group-focus-within:block absolute top-full right-0 mt-1 z-30 min-w-[220px] max-w-[320px] rounded-md border border-amber-300 bg-white shadow-lg px-3 py-2 pointer-events-none"
                >
                  <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 whitespace-nowrap ${
                    reviewVerified ? "text-emerald-700" : "text-amber-800"
                  }`}>
                    {reviewVerified
                      ? `${checks.length} Prüfung${checks.length !== 1 ? "en" : ""} geprüft`
                      : (checks.length === 1
                        ? "1 Prüfung offen"
                        : `${checks.length} Prüfungen offen`)}
                  </div>
                  <ul className="space-y-1">
                    {checks.map((c, ci) => (
                      <li
                        key={ci}
                        className={`text-[11.5px] leading-snug flex items-start gap-1.5 ${
                          reviewVerified ? "text-ink-500 line-through" : "text-ink-800"
                        }`}
                      >
                        <span
                          className={`mt-1 shrink-0 inline-block w-1 h-1 rounded-full ${
                            reviewVerified ? "bg-emerald-500" : "bg-amber-500"
                          }`}
                          aria-hidden="true"
                        />
                        <span className="min-w-0">{c.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </span>
            );
          })()}
          {/* Anrechnen-Switch (Slider-Optik).
              Mockup→Live Phase 5 — Ehemaliges "Anr."-Kompakt-Häkchen
              als vollwertiger Toggle-Switch mit ausgeschriebenem Label,
              damit Sachbearbeiter auf einen Blick sehen, ob eine Zeile
              anrechenbar ist. Behält die selben Semantik wie zuvor
              (Faktor auf 1.0 wenn re-aktiviert und faktor=0 war). */}
          <label
            className="inline-flex shrink-0 items-center gap-1.5 cursor-pointer select-none"
            title={row.anrechenbar
              ? "Diese Zeit wird in die Anrechnung einbezogen"
              : "Diese Zeit wird nicht angerechnet"}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={row.anrechenbar}
              onChange={(e) => {
                const checked = e.target.checked;
                const next = { ...row, anrechenbar: checked };
                if (checked && (!row.anrechnungsfaktor || row.anrechnungsfaktor === 0)) {
                  next.anrechnungsfaktor = 1.0;
                }
                onChange(next);
              }}
              onClick={(e) => e.stopPropagation()}
              className="sr-only"
              data-testid={`vd-row-${index}-compact-anr`}
              aria-label={row.anrechenbar ? "Anrechnen deaktivieren" : "Anrechnen aktivieren"}
            />
            <span
              aria-hidden="true"
              className={[
                "relative inline-block h-4 w-7 rounded-full transition-colors",
                row.anrechenbar ? "bg-status-aktiv-border" : "bg-warm-300",
              ].join(" ")}
            >
              <span
                className={[
                  "absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                  row.anrechenbar ? "translate-x-3.5" : "translate-x-0.5",
                ].join(" ")}
              />
            </span>
            <span
              className={[
                "text-[11px] font-medium whitespace-nowrap",
                row.anrechenbar ? "text-status-aktiv-text" : "text-warm-500",
              ].join(" ")}
            >
              Anrechnen
            </span>
          </label>
          {/* Chevron — öffnet Detail-Drawer */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setRowExpanded((v) => !v);
            }}
            aria-label={rowExpanded ? "Details schließen" : "Details öffnen"}
            data-testid={`vd-row-${index}-compact-toggle`}
            className="shrink-0 text-warm-500 hover:text-ink-900 hover:bg-ink-100 rounded-sm p-1 transition-crisp"
          >
            {rowExpanded
              ? <CaretUp size={14} weight="bold" />
              : <CaretDown size={14} weight="bold" />}
          </button>
          {/* Iter 20.03.P5-fix Runde 7 — Trenner + Abstand zwischen Chevron
              und Trash-Icon. Aus dem UX-Audit P2: Chevron und Trash lagen
              vorher direkt nebeneinander (2 px Gap) mit deckungsgleicher
              Größe → hohe Miss-Click-Wahrscheinlichkeit. Der vertikale
              Divider signalisiert außerdem visuell „andere Kategorie". */}
          <span
            aria-hidden="true"
            className="shrink-0 mx-1.5 h-4 w-px bg-ink-200"
          />
          {/* Delete */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Zeile entfernen"
            data-testid={`vd-row-${index}-compact-remove`}
            className="shrink-0 text-warm-400 hover:text-status-abgelehnt-text hover:bg-rose-50 rounded-sm p-1 transition-crisp"
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      )}
      {/* ==================================================================
          Detail-Content: bleibt UNVERÄNDERT im Detail-Modus. Im Kompakt-
          Modus wird der komplette Block als Drawer NACH dem Kompakt-Header
          gerendert — mit border-top zur visuellen Separation.
          ================================================================== */}
      {(!isCompact || rowExpanded) && (
      <div
        className={isCompact ? "border-t border-ink-100 bg-ink-50/40" : ""}
        data-testid={isCompact ? `vd-row-${index}-detail-drawer` : undefined}
      >
      <div className="grid grid-cols-12 gap-2 p-3 items-start">
        <div className="col-span-12 sm:col-span-5 flex gap-1.5 items-start min-w-0">
          <GermanDateInput
            value={row.datum_von}
            mode="von"
            onChange={(iso) => onChange({ ...row, datum_von: iso })}
            data-testid={`vd-row-${index}-von`}
            className="w-[110px] shrink-0"
            placeholder="TT.MM.JJJJ"
          />
          <span className="text-warm-500 text-sm pt-1.5 shrink-0">–</span>
          <div className="relative flex items-start gap-1 shrink-0">
            <GermanDateInput
              value={row.datum_bis}
              mode="bis"
              onChange={(iso) => {
                // Iter 19.63.21 — Wenn User ein konkretes Bis-Datum eingibt
                // (oder das Feld leert), wird der `was_laufend`-Flag gelöscht.
                // Das Backend setzt ihn beim nächsten Review wieder, falls
                // der User „laufend" wieder einsetzt.
                const next = { ...row, datum_bis: iso };
                if (row.was_laufend && iso !== "") {
                  next.was_laufend = false;
                }
                onChange(next);
              }}
              data-testid={`vd-row-${index}-bis`}
              className="w-[110px]"
              placeholder="TT.MM.JJJJ"
              emptyDisplayText={row.was_laufend ? "laufend" : ""}
            />
            {row.was_laufend && (
              <span
                className="pt-1.5 text-amber-600"
                title={`Im SV-Auszug als „laufend" markiert. Als Bis-Datum wurde 1 Tag vor dem Stichtag verwendet (${stichtagMinusOneLabel}, da der Stichtag ${stichtagLabel} selbst der erste Tag im neuen Job ist). Bitte prüfen, ob die Beschäftigung tatsächlich noch läuft — andernfalls konkretes Bis-Datum eintragen.`}
                data-testid={`vd-row-${index}-laufend-flag`}
                aria-label="Position war im SV-Auszug als laufend markiert"
              >
                <WarningCircle size={20} weight="fill" />
              </span>
            )}
          </div>
          {/* Iter 19.29: Show the duration cell whenever both date inputs
              are filled — independent of the locally-computed months. This
              keeps the "gesamt X" sub-label visible for Stichtag-rejected
              rows (where monthsEffective=0) AND for any future edge case
              where the local monthsBetween calc might surprise. */}
          {Boolean(row.datum_von && (row.datum_bis || row.was_laufend)) && (() => {
            // Iter 19.63.27 / 19.63.69 — Authoritative Display-Strings vom
            // Backend, aber UNABHÄNGIG pro Feld.
            //
            // Bisherig: `hasBackendDisplay = typeof display_raw === "string"
            // || typeof display_anrechenbar === "string"` — bei OR-Logik mit
            // `effLabel = row.display_anrechenbar || "0 T."` erschien
            // fälschlich „0 T." sobald nur `display_raw` gesetzt war (z.B.
            // nach Iter 19.63.69 Defense-Fix bei anrechenbar=false → false).
            // Beim Wieder-Aktivieren-Toggle (anrechenbar=true, alle Caches
            // undefined) lief `hasBackendDisplay=true` weil display_raw
            // erhalten bleibt → effLabel kollabierte auf "0 T.".
            //
            // Fix: Beide Display-Strings UNABHÄNGIG prüfen.
            //   • totalLabel: Backend-display_raw bevorzugen, sonst lokal.
            //   • effLabel: Backend-display_anrechenbar bevorzugen, sonst
            //     lokal (über monthsEffective / effective_period).
            const hasBackendTotal = typeof row.display_raw === "string";
            const hasBackendEff = typeof row.display_anrechenbar === "string";

            let totalLabel;
            if (hasBackendTotal) {
              totalLabel = row.display_raw;
            } else {
              const totalYMD = diffYMD(row.datum_von, row.datum_bis, stichtag);
              totalLabel = formatYMD(totalYMD);
            }

            let effLabel;
            if (hasBackendEff) {
              effLabel = row.display_anrechenbar;
            } else if (isPartial) {
              const ep = row.effective_period;
              if (ep && ep.von && ep.bis) {
                const epMonths = monthsBetween30360(ep.von, ep.bis, stichtag);
                if (Math.abs(epMonths - monthsEffective) <= 0.035) {
                  effLabel = formatYMD(diffYMD(ep.von, ep.bis, stichtag));
                } else {
                  effLabel = formatYMD(effectiveDurationYMD30360(monthsEffective));
                }
              } else {
                effLabel = formatYMD(effectiveDurationYMD30360(monthsEffective));
              }
            } else {
              effLabel = totalLabel;
            }
            const primary = isZero ? "0 T." : effLabel;
            const secondary = showEff ? `gesamt ${totalLabel}` : null;
            const primaryClass = isZero
              ? "text-warm-500 line-through"
              : (isPartial ? "text-brand-600" : "text-ink-900");
            const pctHint = faktor > 0 && faktor < 1
              ? ` · Faktor ${Math.round(faktor * 100)} %`
              : "";
            return (
              <span
                className="pt-1 pl-1 w-[110px] shrink-0 leading-tight text-left whitespace-nowrap overflow-hidden"
                title={
                  isZero
                    ? `Gesamtdauer: ${totalLabel} · nicht anrechenbar`
                    : isPartial
                      ? `Gesamtdauer: ${totalLabel} · davon anrechenbar: ${effLabel}${pctHint}`
                      : `Gesamtdauer: ${totalLabel}`
                }
                data-testid={`vd-row-${index}-duration`}
              >
                <span className={`block text-sm font-semibold tabular-nums ${primaryClass}`}>
                  {primary}
                </span>
                {secondary && (
                  <span className="block text-[10px] text-warm-500 tabular-nums">
                    {secondary}
                  </span>
                )}
              </span>
            );
          })()}
        </div>
        {/* Iter 19.66.6 — Anrechnungsfaktor in %, kompakt in der Hauptzeile
            zwischen Dauer-Block und Arbeitgeber. Bei nicht-anrechenbarer
            Zeile (anrechenbar=false) leere Platzhalter-Spalte, damit das
            Grid-Layout stabil bleibt. Pill + „Sperre aufheben"-Link werden
            separat unter der Zeile gerendert (siehe Iter 19.66.6 weiter
            unten), damit der Hauptzeile-Block visuell ruhig bleibt. */}
        <div
          className="col-span-3 sm:col-span-1 flex items-center gap-0.5"
          data-testid={`vd-row-${index}-faktor-wrap`}
        >
          {row.anrechenbar ? (
            <>
              <input
                type="number"
                min={0}
                max={100}
                step={5}
                value={Math.round(faktor * 100)}
                onChange={(e) => {
                  const raw = e.target.value;
                  const pct = raw === ""
                    ? 0
                    : Math.max(0, Math.min(100, Number(raw) || 0));
                  const newFaktor = pct / 100;
                  onChange({
                    ...row,
                    anrechnungsfaktor: newFaktor,
                    _factor_manual: true,
                    months_anrechenbar: undefined,
                    effective_days_anrechenbar: undefined,
                    effective_period: undefined,
                    dauer_anrechenbar_jahre: undefined,
                    dauer_anrechenbar_monate: undefined,
                    dauer_anrechenbar_tage: undefined,
                    display_raw: undefined,
                    display_anrechenbar: undefined,
                    display_anrechenbar_pre_cap: undefined,
                    dauer_anrechenbar_pre_cap_jahre: undefined,
                    dauer_anrechenbar_pre_cap_monate: undefined,
                    dauer_anrechenbar_pre_cap_tage: undefined,
                    _cap_reduced: undefined,
                    _cap_zeroed: undefined,
                    // Iter 20.36.f9 — Idempotenz-Marker der Sonderregeln
                    // ebenfalls invalidieren. Ohne diesen Reset würde der
                    // Backend-Recalc den manuellen Faktor via
                    // `_revert_altersfaktor`/`_revert_wt` auf den zuvor
                    // gespeicherten `_*_previous_factor` zurückrollen und
                    // die manuelle Eingabe ginge verloren. Danach re-
                    // applied das Backend die aktuellen Regeln (Alters-
                    // faktor, WT-Kanzlei) frisch auf den neuen Manual-
                    // Wert — halbiert also z.B. korrekt zu 0.5, wenn die
                    // 5-J-Ausnahme durch andere Toggles wegfällt.
                    _altersfaktor_applied: false,
                    _altersfaktor_previous_factor: null,
                    _altersfaktor_split: undefined,
                    _wt_non_kanzlei_reduced: false,
                    _wt_previous_factor: null,
                  });
                }}
                data-testid={`vd-row-${index}-faktor-percent`}
                aria-label="Anrechnungsfaktor in Prozent"
                title="Anrechnungsfaktor in % (0–100). Standard: 100 %. Manuell anpassbar, z.B. auf 50 % für hälftige Anrechnung."
                className="w-12 text-xs tabular-nums rounded-sm border border-ink-300 bg-white px-1 py-1.5 text-right focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
              />
              <span className="text-xs text-warm-500 select-none">%</span>
            </>
          ) : (
            <span className="text-xs text-warm-400">—</span>
          )}
        </div>
        <div className="col-span-12 sm:col-span-2">
          <input
            type="text"
            value={row.arbeitgeber}
            placeholder="Arbeitgeber"
            onChange={(e) => onChange({ ...row, arbeitgeber: e.target.value })}
            className="w-full text-sm rounded-sm border border-ink-300 px-2 py-1.5 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
            data-testid={`vd-row-${index}-arbeitgeber`}
          />
        </div>
        <div className="col-span-12 sm:col-span-2">
          <input
            type="text"
            value={row.taetigkeit}
            placeholder="Tätigkeit"
            onChange={(e) => onChange({ ...row, taetigkeit: e.target.value })}
            className="w-full text-sm rounded-sm border border-ink-300 px-2 py-1.5 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
            data-testid={`vd-row-${index}-taetigkeit`}
          />
          {/* Iter 19.63.76 / 19.63.77 — Praktikum-Pill-Badge.
              Sichtbar NUR wenn ANR-Häkchen aktiv ist (User-Wunsch:
              wer das Praktikum bewusst nicht anrechnen will, soll
              keinen Hinweis mehr sehen — Badge erscheint erst wieder,
              wenn ANR-Häkchen wieder aktiviert wird).

              Farbe wechselt zwischen Amber (Typ noch zu wählen) und
              Smaragd (Typ bereits gewählt), passend zum Akzent-Balken
              und zum Inline-Selector. */}
          {isPraktikum && row.anrechenbar && (
            <div className="mt-1">
              <span
                data-testid={`vd-row-${index}-praktikum-badge`}
                className={[
                  "inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm border",
                  praktikumUnresolved
                    ? "border-amber-500 bg-amber-100 text-amber-900"
                    : "border-emerald-400 bg-emerald-50 text-emerald-800",
                ].join(" ")}
                title={
                  praktikumUnresolved
                    ? "Praktikum erkannt — Anstellungs-Typ noch zu wählen"
                    : `Praktikum als ${praktikumTyp === "angestellter" ? "Angestellter" : "Arbeiter"} eingestuft`
                }
              >
                Praktikum
              </span>
            </div>
          )}
          {/* Iter 19.63.80 — Faktor-Provenienz-Badge.
              Zeigt deterministisch an, woher der aktuelle Anrechnungs-
              Faktor kommt (KV-Regel, KV-Praktikum, Manuell, Default).
              Default → User-Aufmerksamkeit empfohlen (Anstellungstyp
              eventuell nicht eindeutig aus der Quelle erkennbar).
              Bei !row.anrechenbar (Häkchen aus) wird das Badge ausgeblendet
              (siehe `classifyFactorProvenance` → "n/a"). */}
          {row.anrechenbar && (
            <div className="mt-1 flex items-center flex-wrap gap-1.5">
              <FaktorProvenienzBadge
                provenance={factorProvenance}
                row={row}
                faktorRegeln={faktorRegeln}
                testIdPrefix={`vd-row-${index}`}
              />
              {/* Iter 19.66.7 — „Sperre aufheben"-Link erscheint direkt
                  rechts neben dem Provenance-Badge, wenn `_factor_manual=true`
                  (= User hat den Faktor manuell via %-Input überschrieben).
                  Die separate Pill „Faktor manuell überschrieben" wurde
                  bewusst entfernt, weil der Provenance-Badge bereits
                  „MANUELL" zeigt — Doppelinfo war redundant. */}
              {row._factor_manual === true && (
                <button
                  type="button"
                  onClick={() => {
                    // Iter 19.66.7 — Reset-Click: nicht nur _factor_manual=false
                    // setzen (das löst KEIN sichtbares Re-Eval aus, weil
                    // classifyFactorProvenance bei f≠1.0 weiter „manual" returnt).
                    // Stattdessen: applyKvFactorRule erneut auf die aktuelle
                    // Tätigkeit anwenden — falls das KV eine passende Regel
                    // liefert (DIRECT-Match oder Default-voll-Fallback), wird
                    // Faktor, Anrechenbarkeit und Begründung auf den KV-Wert
                    // gesetzt. Andernfalls bleibt der manuelle Wert erhalten,
                    // aber _factor_manual=false → Badge wechselt auf „Annahme"
                    // bei ambivalenten oder „KV-Regel" bei expliziten Tätigkeiten.
                    const rule = applyKvFactorRule(
                      row.taetigkeit || "",
                      faktorRegeln || {},
                    );
                    const reset = {
                      ...row,
                      _factor_manual: false,
                      months_anrechenbar: undefined,
                      effective_days_anrechenbar: undefined,
                      effective_period: undefined,
                      dauer_anrechenbar_jahre: undefined,
                      dauer_anrechenbar_monate: undefined,
                      dauer_anrechenbar_tage: undefined,
                      display_raw: undefined,
                      display_anrechenbar: undefined,
                      display_anrechenbar_pre_cap: undefined,
                      dauer_anrechenbar_pre_cap_jahre: undefined,
                      dauer_anrechenbar_pre_cap_monate: undefined,
                      dauer_anrechenbar_pre_cap_tage: undefined,
                      _cap_reduced: undefined,
                      _cap_zeroed: undefined,
                      // Iter 20.36.f9 — auch bei „Sperre aufheben" die
                      // Sonderregel-Marker resetten, damit die Regeln
                      // frisch angewendet werden.
                      _altersfaktor_applied: false,
                      _altersfaktor_previous_factor: null,
                    _altersfaktor_split: undefined,
                      _wt_non_kanzlei_reduced: false,
                      _wt_previous_factor: null,
                    };
                    if (rule) {
                      reset.anrechenbar = rule.anrechenbar;
                      reset.anrechnungsfaktor = rule.faktor;
                      reset.anrechenbarkeit_begruendung = rule.begruendung;
                    }
                    onChange(reset);
                  }}
                  data-testid={`vd-row-${index}-faktor-reset`}
                  title="Manuelle Sperre aufheben — KV-Regel wird wieder angewendet (falls die Tätigkeit dort matcht)."
                  className="text-[10px] text-warm-500 underline decoration-dotted underline-offset-2 hover:text-ink-900 transition-crisp"
                >
                  Sperre aufheben
                </button>
              )}
            </div>
          )}
          {/* Iter 19.63.4 — Konsolidierungs-Badge: zeigt an, wenn diese Zeile
              aus mehreren Original-SV-Auszug-Zeilen gemergt wurde. */}
          {Array.isArray(row.source_items) && row.source_items.length > 1 && (
            <div className="mt-1">
              <KonsolidiertBadge sourceItems={row.source_items} />
            </div>
          )}
          {/* Iter 19.68 — Extraction-Quality-Badges (Soft-Warning bei
              Hallucination-Verdacht / Adjazenz-Heuristik, Audit-Spur bei
              vorsichtiger Auto-Korrektur). Nicht-destruktiv: keine Daten
              werden ohne klare Doppel-Bestätigung überschrieben.
              Iter 19.68.3 — Bekommt zusätzlich `onAckToggle` (User klickt
              „Position geprüft & bestätigt"-Checkbox) und versteckt sich,
              wenn bereits acknowledged. */}
          <QualityBadges
            row={{ ...row, __index: index }}
            onAckToggle={(checked) => _rawOnChange(index, { ...row, _quality_ack: checked })}
          />
        </div>
        <div className="col-span-10 sm:col-span-1 flex items-center">
          <label
            className={[
              "inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider cursor-pointer select-none px-2 py-1 rounded-sm border transition-crisp whitespace-nowrap",
              row.anrechenbar
                ? "bg-status-aktiv-bg border-status-aktiv-border text-status-aktiv-text"
                : "bg-warm-50 border-warm-300 text-warm-500",
            ].join(" ")}
          >
            <input
              type="checkbox"
              checked={row.anrechenbar}
              onChange={(e) => {
                // Iter 19.63.71 — Backend setzt bei anrechenbar=false den
                // Faktor deterministisch auf 0.0 zurück (Teil der Math-
                // Konvention im backend `_coerce_items`, NICHT anfassen).
                // Beim Wieder-Aktivieren der ANR-Checkbox müssen wir den
                // Faktor im FE-State restoren — sonst sendet `runRecalcSum`
                // {anrechenbar:true, anrechnungsfaktor:0} ans Backend, das
                // konsequenterweise 0 Tage zurückrechnet (correct math
                // given factor=0).
                //
                // Default: 1.0 (voll anrechenbar). Der User kann den Faktor
                // anschließend manuell anpassen (z.B. auf 0.5 für hälftig)
                // — die Anzeige des Faktors befindet sich in der
                // Begründungs-Zeile darunter.
                const checked = e.target.checked;
                const next = { ...row, anrechenbar: checked };
                if (
                  checked &&
                  (!row.anrechnungsfaktor || row.anrechnungsfaktor === 0)
                ) {
                  next.anrechnungsfaktor = 1.0;
                }
                onChange(next);
              }}
              className="h-3 w-3 accent-status-aktiv-border cursor-pointer"
              data-testid={`vd-row-${index}-anr`}
            />
            Anr.
          </label>
        </div>
        {/* Iter 20.36.f14.24 — Detail-Aktions-Buttons (Chevron zum
            Begründungs-Toggle + Trash zum Löschen) entfernt. Beide waren
            redundant zu den identischen Buttons in der kompakten Row-
            Header-Zeile. `open`-State ist außerdem entfernt (siehe
            useState-Cleanup weiter oben) — die Begründung wird jetzt
            immer angezeigt, sobald die Zeile aufgeklappt ist. */}
      </div>
      {/* Iter 19.66.7 — Pill „Faktor manuell überschrieben" entfernt
          (Redundanz zum Provenance-Badge „MANUELL"). „Sperre aufheben"-
          Link sitzt jetzt rechts neben dem Badge in der Hauptzeile —
          siehe oben Z. ~492. */}
      {/* Iter 19.63.22 — Persistenter Inline-Hinweistext für „laufend"-Zeilen.
          Damit der User OHNE Hover sofort sieht, warum das Datum als „laufend"
          angezeigt wird und welcher Stichtag verwendet wurde. */}
      {row.was_laufend && (
        <div
          className="px-3 pb-2 -mt-1 flex items-start gap-1.5 text-[11px] text-amber-700 italic"
          data-testid={`vd-row-${index}-laufend-hint`}
        >
          <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
          <span>
            Im SV-Auszug als &bdquo;laufend&ldquo; markiert &mdash; als Bis-Datum wird{" "}
            <strong>{stichtagMinusOneLabel}</strong> (1 Tag vor Stichtag{" "}
            {stichtagLabel}) verwendet, weil der Stichtag selbst bereits der
            erste Tag im neuen Job ist. Bitte prüfen, ob die Beschäftigung
            tatsächlich noch läuft. Andernfalls konkretes Bis-Datum eintragen &mdash;
            der Hinweis verschwindet dann automatisch.
          </span>
        </div>
      )}
      {/* Iter 19.63.101 — Cap-Bypass „Selber Arbeitgeber"-Checkbox.
          Sichtbar nur wenn KV diese Sonderregel kennt UND der Cap durch
          andere Items bereits erreicht ist (oder die Checkbox schon
          gesetzt war — sonst wäre kein „Untoggle" mehr möglich).
          Bei Aktivierung: Position zählt VOLL zur Anrechnung, OHNE auf den
          Cap einzuzahlen (Backend setzt `_cap_bypassed=true` und schließt
          das Item vom Cap-Walk aus). Soft-Warning erscheint inline, falls
          die zeitliche Kette (Lücke ≤ 24 M.) verletzt wird — Bypass bleibt
          dennoch aktiv, Sachbearbeiter entscheidet. */}
      {showBypassCheckbox && (
        <div
          className="px-3 pb-2 -mt-1"
          data-testid={`vd-row-${index}-bypass-wrap`}
        >
          <label
            className={[
              "inline-flex items-start gap-2 text-[11px] leading-snug rounded-sm border px-2 py-1.5 cursor-pointer select-none transition-crisp",
              bypassActive
                ? "border-amber-400 bg-amber-50/70 text-amber-900"
                : "border-ink-200 bg-ink-50 hover:bg-warm-50 text-ink-700",
            ].join(" ")}
          >
            <input
              type="checkbox"
              checked={Boolean(row.cap_bypass)}
              onChange={(e) => {
                // Iter 19.63.101 — Bei Toggle Backend-Caches strippen, damit
                // `runRecalcSum` den Item neu durch den Cap-Walk schickt
                // (sonst zeigt die Zeile alte cap-reduzierte Werte trotz
                // jetzt aktivem Bypass). Aggregate wird in StepThree via
                // setBackendAggregate(null) invalidiert (Dirty-State).
                onChange({
                  ...row,
                  cap_bypass: e.target.checked,
                  // Marker zurücksetzen — wird beim nächsten Recalc neu gesetzt.
                  _cap_bypassed: undefined,
                  _bypass_chain_warning: undefined,
                  _bypass_gap_to_next_months: undefined,
                  // Cap-Reduktions-Felder strippen (Bypass-Item darf keine
                  // alte Cap-Reduktion mehr zeigen).
                  _cap_reduced: undefined,
                  _cap_zeroed: undefined,
                  months_anrechenbar: undefined,
                  effective_days_anrechenbar: undefined,
                  effective_period: undefined,
                  dauer_anrechenbar_jahre: undefined,
                  dauer_anrechenbar_monate: undefined,
                  dauer_anrechenbar_tage: undefined,
                  display_anrechenbar: undefined,
                  display_anrechenbar_pre_cap: undefined,
                });
              }}
              className="h-3 w-3 mt-0.5 accent-amber-500 cursor-pointer shrink-0"
              data-testid={`vd-row-${index}-cap-bypass`}
            />
            <span>
              <strong className="font-semibold uppercase tracking-wider text-[10px]">
                Selber Arbeitgeber
              </strong>
              {" — "}
              {bypassActive
                ? "Position zählt ÜBER die Höchstanrechnungsdauer hinaus (KV-Sonderregel)."
                : "Falls dieselbe Anstellung beim NEUEN Arbeitgeber vorlag, hier markieren. Position zählt dann zusätzlich zur Höchstanrechnungsdauer (KV-Sonderregel)."}
            </span>
          </label>
        </div>
      )}
      {/* Iter 20.36.f2 — WT-Kanzlei-Kombiregel-Checkbox.
          Sichtbar nur bei KVs mit `wt_kanzlei_kombiregel` (typischerweise
          Steuerberater/WT-KV). Der Sachbearbeiter markiert Vordienstzeiten,
          die in einer Wirtschaftstreuhand-Kanzlei erbracht wurden — solche
          zählen laut KV bis zur Höchstanrechnungsdauer (`max_jahre`) VOLL,
          während Zeiten außerhalb einer WT-Kanzlei nur mit reduziertem
          Faktor (typisch 50 %) im `zeitfenster_jahre`-Fenster berücksichtigt
          werden. Aktuell rein informativ — die konkrete Auswirkung wird via
          `CapBypassHints` unterhalb der Zeile erläutert; die Math-Engine
          bleibt unverändert. */}
      {showWtKanzleiCheckbox && (
        <div
          className="px-3 pb-2 -mt-1"
          data-testid={`vd-row-${index}-wt-kanzlei-wrap`}
        >
          <label
            className={[
              "inline-flex items-start gap-2 text-[11px] leading-snug rounded-sm border px-2 py-1.5 cursor-pointer select-none transition-crisp",
              wtKanzleiActive
                ? "border-emerald-400 bg-emerald-50/70 text-emerald-900"
                : "border-ink-200 bg-ink-50 hover:bg-warm-50 text-ink-700",
            ].join(" ")}
          >
            <input
              type="checkbox"
              checked={wtKanzleiActive}
              onChange={(e) => {
                onChange({
                  ...row,
                  wt_kanzlei: e.target.checked,
                });
              }}
              className="h-3 w-3 mt-0.5 accent-emerald-500 cursor-pointer shrink-0"
              data-testid={`vd-row-${index}-wt-kanzlei`}
            />
            <span>
              <strong className="font-semibold uppercase tracking-wider text-[10px]">
                In WT-Kanzlei erbracht
              </strong>
              {" — "}
              {wtKanzleiActive
                ? "Position zählt laut KV voll bis zur Höchstanrechnungsdauer (WT-Kanzlei-Sonderregel)."
                : "Hier markieren, wenn diese Vordienstzeit in einer Wirtschaftstreuhand-Kanzlei erbracht wurde. Zeiten in einer WT-Kanzlei werden voll bis zur Höchstanrechnungsdauer angerechnet."}
            </span>
          </label>
        </div>
      )}
      {/* Iter 19.66.3 — KV-spezifische Cap-Bypass-Sonderregel-Hinweise.
          Iter 19.66.4 — Sichtbarkeit verschärft: erscheinen erst, NACHDEM
          der User das „Selber Arbeitgeber"-Hakerl gesetzt hat. Vorher war
          der Bereich auch sichtbar, sobald die Checkbox erschien — User-
          Feedback: zu viel visuelles Rauschen. Math-Engine unverändert.
          Iter 20.36.f2 — Nur die „Selber-AG"-verwandten Regel-Typen
          zeigen; die WT-Kanzlei-Kombiregel wird darunter separat unter
          ihrer eigenen Checkbox erläutert. */}
      {Boolean(row.cap_bypass) &&
        Array.isArray(capBypassSonderregeln) &&
        capBypassSonderregeln.length > 0 && (
          <CapBypassHints
            sonderregeln={capBypassSonderregeln.filter(
              (r) => r && r.typ !== "wt_kanzlei_kombiregel",
            )}
            rowIndex={index}
          />
        )}
      {/* Iter 20.36.f2 — WT-Kanzlei-Regel-Hint. Nur sichtbar, wenn die
          Zeile via „In WT-Kanzlei erbracht"-Checkbox markiert ist UND
          der KV die Sonderregel `wt_kanzlei_kombiregel` konfiguriert
          hat. */}
      {wtKanzleiActive &&
        Array.isArray(capBypassSonderregeln) &&
        capBypassSonderregeln.some(
          (r) =>
            r &&
            r.typ === "wt_kanzlei_kombiregel" &&
            r.aktiv !== false,
        ) && (
          <CapBypassHints
            sonderregeln={capBypassSonderregeln.filter(
              (r) => r && r.typ === "wt_kanzlei_kombiregel",
            )}
            rowIndex={index}
          />
        )}
      {/* Iter 19.63.101 — Soft-Warning bei Kettenlücke > 24 Monate.
          Backend setzt `_bypass_chain_warning` mit konkretem Text
          ("Lücke zur nächsten Bypass-Position = X,Y Monate (> 24 M. KV-Grenzwert)").
          Bypass bleibt aktiv — der Sachbearbeiter entscheidet.

          Iter 19.66.4 — Sichtbarkeit zweifach verschärft:
          (a) Nur sichtbar, wenn das Selber-AG-Hakerl gesetzt ist.
          (b) Wenn das KV neue Sonderregeln (`cap_bypass_sonderregeln`)
              konfiguriert hat, wird die Warnung NUR gezeigt, wenn eine
              aktive Lückenregel-Sonderregel dort enthalten ist. Damit
              werden falsche Lücken-Warnungen für KVs unterdrückt, die
              z.B. nur die Vorarbeiterzeit-Sonderregel definiert haben
              (User-Beispiel: KV Gewerbe). Legacy-KVs ohne Sonderregeln
              behalten das alte Verhalten (volle Warnung sichtbar). */}
      {bypassChainWarning && Boolean(row.cap_bypass) && (() => {
        const sondList = Array.isArray(capBypassSonderregeln)
          ? capBypassSonderregeln
          : [];
        const hasAnySonderregeln = sondList.some(
          (r) => r && r.aktiv !== false,
        );
        const hasLueckenregelSonderregel = sondList.some(
          (r) =>
            r &&
            r.typ === "selber_arbeitgeber_lueckenregel" &&
            r.aktiv !== false,
        );
        // Legacy-KV (keine neuen Sonderregeln) ODER neue Lückenregel aktiv
        if (hasAnySonderregeln && !hasLueckenregelSonderregel) return null;
        return (
          <div
            className="px-3 pb-2 -mt-1 flex items-start gap-1.5 text-[11px] text-amber-700 italic"
            data-testid={`vd-row-${index}-bypass-warning`}
            role="note"
          >
            <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
            <span>{bypassChainWarning}</span>
          </div>
        );
      })()}
      {/* Iter 19.63.74 — Praktikum-Anstellungstyp-Selector.
          Sichtbar wenn:
            • Tätigkeit enthält ein Praktikum-Synonym (Lebenslauf-Pfad), UND
            • User hat noch nicht zwischen Angestellter/Arbeiter gewählt.
          Sobald gewählt: Tätigkeit + Faktor + Begründung werden auto-
          gesetzt (gemäß KV-Regel), der Selector verschwindet und ein
          schmaler Status-Indikator zeigt die Wahl mit „[ändern]"-Option. */}
      {isPraktikum && praktikumUnresolved && (
        <div
          className="px-3 pb-3 -mt-1 flex flex-wrap items-center gap-2 text-[12px] text-amber-900"
          data-testid={`vd-row-${index}-praktikum-typ-select`}
        >
          <WarningCircle size={14} weight="fill" className="shrink-0 text-amber-600" />
          <span className="font-medium">
            Praktikum erkannt — bitte Anstellungs-Typ wählen:
          </span>
          <div className="inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                // Iter 20.03.P5-fix — Punkt 3 aus User-Report Martina Fall 4.
                // Nach Typ-Wahl: Drawer im Kompaktmodus zusammenklappen +
                // `_quality_ack=true` implizit setzen (Zeile ist damit als
                // „geprüft" markiert → Warn-Dot verschwindet, Row-Border
                // neutral, Provenance-Chip flippt via classifyFactorProvenance
                // von amber „PRAKTIKUM" auf emerald „KV-Prakt.").
                onChange({
                  ...setPraktikumTyp(row, "angestellter", faktorRegeln, praktikumRegeln),
                  _quality_ack: true,
                });
                if (isCompact) setRowExpanded(false);
              }}
              data-testid={`vd-row-${index}-praktikum-typ-angestellter`}
              className="px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider rounded-sm border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 transition-crisp"
            >
              Angestellter
            </button>
            <button
              type="button"
              onClick={() => {
                onChange({
                  ...setPraktikumTyp(row, "arbeiter", faktorRegeln, praktikumRegeln),
                  _quality_ack: true,
                });
                if (isCompact) setRowExpanded(false);
              }}
              data-testid={`vd-row-${index}-praktikum-typ-arbeiter`}
              className="px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider rounded-sm border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 transition-crisp"
            >
              Arbeiter
            </button>
          </div>
          <span className="text-[11px] italic text-amber-700">
            Anrechnungsfaktor wird laut hinterlegter KV-Regel automatisch gesetzt.
          </span>
        </div>
      )}
      {isPraktikum && !praktikumUnresolved && praktikumTyp && (
        <div
          className="px-3 pb-2 -mt-1 flex items-center gap-1.5 text-[11px] text-emerald-800"
          data-testid={`vd-row-${index}-praktikum-typ-resolved`}
        >
          <span className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 text-white text-[9px] font-bold">
            ✓
          </span>
          <span>
            Praktikum als{" "}
            <strong>
              {praktikumTyp === "angestellter" ? "Angestellter" : "Arbeiter"}
            </strong>{" "}
            eingestuft (Faktor {Number(row.anrechnungsfaktor ?? 1).toString().replace(".", ",")})
          </span>
          <button
            type="button"
            onClick={() => {
              // Iter 19.63.74 — Reset: Prefix entfernen, Faktor zurücksetzen,
              // Backend-Cache invalidieren. Damit ist der Row-State sauber,
              // bis der User erneut „Angestellter" oder „Arbeiter" wählt.
              const cleanTaet = stripPraktikumTypPrefix(row.taetigkeit || "");
              onChange({
                ...row,
                praktikum_anstellungstyp: undefined,
                taetigkeit: cleanTaet,
                anrechnungsfaktor: 1.0,
                anrechenbarkeit_begruendung: "",
                months_anrechenbar: undefined,
                effective_days_anrechenbar: null,
                dauer_anrechenbar_jahre: undefined,
                dauer_anrechenbar_monate: undefined,
                dauer_anrechenbar_tage: undefined,
                display_raw: undefined,
                display_anrechenbar: undefined,
                effective_period: undefined,
              });
            }}
            data-testid={`vd-row-${index}-praktikum-typ-reset`}
            className="text-emerald-700 hover:text-emerald-900 underline underline-offset-2 transition-crisp"
          >
            ändern
          </button>
        </div>
      )}
      {/* Iter 20.36.f14.12 — Präsenzdienst-Regel-Warn-Balken.
          Sichtbar, wenn Backend `_praesenzdienst_needs_review=true`
          gesetzt hat (KV-Sonderregel `praesenzdienst_regel`, Modus
          `gesetzlich`).
          Iter 20.36.f14.14 — „Geprüft"-Toggle entfernt (User-Wunsch);
          zusätzlich rendert der kompakte Zeilen-Header nun ein Warn-
          Icon (Kreis + Rufzeichen), sodass die Umschließungs-Prüfung
          auch bei zugeklappter Zeile sichtbar bleibt. Die Warnung ist
          jetzt ein reiner Info-Hinweis — Sachbearbeiter deaktiviert
          „Anrechnen" manuell, falls die Umschließung nicht gegeben ist. */}
      {row._praesenzdienst_needs_review === true && (
        <div
          className="px-3 pb-2 -mt-1"
          data-testid={`vd-row-${index}-praesenzdienst-warn`}
        >
          <div
            className="rounded-sm border-l-4 border-amber-400 bg-amber-50 px-2.5 py-2 flex items-start gap-2"
            role="note"
            aria-label="Präsenzdienst-Umschließung prüfen"
          >
            <WarningCircle
              size={14}
              weight="fill"
              className="mt-0.5 shrink-0 text-amber-600"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1 text-[11.5px] leading-snug text-amber-900">
              <div className="font-semibold mb-0.5">
                Präsenz-/Zivildienst — Umschließung prüfen
              </div>
              Diese Zeile ist laut KV voll anrechenbar, sie gilt jedoch
              nur dann, wenn sie
              <strong className="font-semibold">
                {" "}durch ein Dienst- oder Lehrverhältnis umschlossen
              </strong>
              {" "}ist. Bitte prüfen und ggf. &bdquo;Anrechnen&ldquo;
              deaktivieren.
            </div>
          </div>
        </div>
      )}
      {/* Iter 20.36.f14 — Altersfaktor Boundary-Split-Visualisierung.
          Sichtbar, wenn Backend `_altersfaktor_split` gesetzt hat (die
          Zeile überspannt die Zeitfenster-Grenze, z.B. 10 Jahre vor
          Stichtag). Zeigt transparent, wie der Gesamt-Anrechnungsfaktor
          aus zwei Teilanteilen (VOR Grenze reduziert, AB Grenze voll)
          zusammensetzt. Immer sichtbar (unabhängig vom Begründungs-
          Toggle), weil die Aufschlüsselung essenziell für die
          Nachvollziehbarkeit des KV-Regelwerks ist.

          Iter 20.36.f14.7 — Guard vereinfacht: Split wird nur gerendert,
          wenn `_altersfaktor_applied === true` UND das Split-Meta ein
          Objekt ist. Der äußere `_altersfaktor_applied`-Marker kommt
          direkt vom Backend und ist über den `in`-Check in `shared.js`
          (Iter 20.36.f14.6) autoritär gegenüber dem Frontend-Fallback.
          Der Math.abs-Konsistenz-Check aus f14.6 wurde entfernt: er hat
          in edge-cases (manueller Faktor-Reset, dann WT-Kanzlei-Toggle)
          fälschlich das Rendering blockiert, obwohl das Backend die
          Regel korrekt appliziert hatte. */}
      {row._altersfaktor_applied === true &&
        row._altersfaktor_split &&
        typeof row._altersfaktor_split === "object" && (() => {
        const split = row._altersfaktor_split;
        const alt = split.alt || {};
        const neu = split.neu || {};
        const boundaryLabel = (() => {
          const b = String(split.boundary || "").trim();
          const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(b);
          return m ? `${m[3]}.${m[2]}.${m[1]}` : b || "—";
        })();
        const altFaktorPct = Math.round(Number(alt.faktor ?? 0.5) * 100);
        const neuFaktorPct = Math.round(Number(neu.faktor ?? 1.0) * 100);
        const fmtYmd = (y, m, t) => {
          const parts = [];
          if (y > 0) parts.push(`${y} J.`);
          if (m > 0) parts.push(`${m} M.`);
          if (t > 0 || parts.length === 0) parts.push(`${t} T.`);
          return parts.join(" ");
        };
        const altDauer = fmtYmd(
          Number(alt.dauer_jahre || 0),
          Number(alt.dauer_monate || 0),
          Number(alt.dauer_tage || 0),
        );
        const altDauerAnr = fmtYmd(
          Number(alt.dauer_anr_jahre || 0),
          Number(alt.dauer_anr_monate || 0),
          Number(alt.dauer_anr_tage || 0),
        );
        const neuDauer = fmtYmd(
          Number(neu.dauer_jahre || 0),
          Number(neu.dauer_monate || 0),
          Number(neu.dauer_tage || 0),
        );
        const neuDauerAnr = fmtYmd(
          Number(neu.dauer_anr_jahre || 0),
          Number(neu.dauer_anr_monate || 0),
          Number(neu.dauer_anr_tage || 0),
        );
        const fmtDate = (iso) => {
          if (!iso) return "";
          const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
          return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
        };
        const altVon = fmtDate(alt.von);
        const altBis = fmtDate(alt.bis);
        const neuVon = fmtDate(neu.von);
        const neuBis = neu.was_laufend ? "laufend" : fmtDate(neu.bis);
        return (
          <div
            className="px-3 pb-2 -mt-1"
            data-testid={`vd-row-${index}-altersfaktor-split`}
          >
            <div
              className="rounded-sm border border-indigo-200 bg-indigo-50/40 px-2.5 py-2"
              role="note"
              aria-label="Altersfaktor-Aufteilung an Zeitfenster-Grenze"
            >
              <div className="flex items-start gap-1.5 mb-1.5">
                <WarningCircle
                  size={13}
                  weight="fill"
                  className="mt-0.5 shrink-0 text-indigo-500"
                  aria-hidden="true"
                />
                <div className="text-[11px] leading-snug text-indigo-900">
                  <strong className="font-semibold">
                    Altersfaktor-Aufteilung an der Zeitfenster-Grenze
                    ({boundaryLabel})
                  </strong>
                  {" — "}Vordienstzeit überspannt die KV-Grenze; nur der
                  Anteil VOR der Grenze wird auf {altFaktorPct} % reduziert,
                  der Anteil AB der Grenze bleibt bei {neuFaktorPct} %.
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <div
                  className="rounded-sm border border-amber-300 bg-amber-50/60 px-2 py-1.5"
                  data-testid={`vd-row-${index}-altersfaktor-split-alt`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900">
                      Vor Grenze
                    </span>
                    <span className="text-[11px] font-semibold text-amber-900 tabular-nums">
                      {altFaktorPct} %
                    </span>
                  </div>
                  <div className="text-[11px] font-mono text-ink-800 tabular-nums leading-tight">
                    {altVon} – {altBis}
                  </div>
                  <div className="text-[10px] text-ink-600 leading-tight mt-0.5">
                    brutto <span className="tabular-nums">{altDauer}</span>
                    {" · "}anrechenbar{" "}
                    <span className="tabular-nums font-semibold text-amber-900">
                      {altDauerAnr}
                    </span>
                  </div>
                </div>
                <div
                  className="rounded-sm border border-emerald-300 bg-emerald-50/60 px-2 py-1.5"
                  data-testid={`vd-row-${index}-altersfaktor-split-neu`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-900">
                      Ab Grenze
                    </span>
                    <span className="text-[11px] font-semibold text-emerald-900 tabular-nums">
                      {neuFaktorPct} %
                    </span>
                  </div>
                  <div className="text-[11px] font-mono text-ink-800 tabular-nums leading-tight">
                    {neuVon} – {neuBis}
                  </div>
                  <div className="text-[10px] text-ink-600 leading-tight mt-0.5">
                    brutto <span className="tabular-nums">{neuDauer}</span>
                    {" · "}anrechenbar{" "}
                    <span className="tabular-nums font-semibold text-emerald-900">
                      {neuDauerAnr}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Iter 20.36.f14.24 — Begründungs-Block jetzt IMMER sichtbar
          (früher gate't durch `open`-useState, das mit dem entfernten
          Chevron-Toggle wegfiel). Sobald die Zeile aufgeklappt ist,
          zeigen wir den kompletten Detail-Content inkl. Begründung. */}
      {(
        <div className="px-3 pb-3 pt-0" data-testid={`vd-row-${index}-begruendung-wrap`}>
          {/* Iter 19.63.86 — ANNAHME-Hinweis als sichtbare Info-Zeile
              (vorher nur als Hover-Tooltip am Provenienz-Badge). Wird
              ausschließlich bei `factorProvenance === "default"` gezeigt
              und ergänzt die LLM-Begründungs-Textarea um den Standard-
              Hinweis, dass eine Annahme getroffen wurde. Read-only,
              damit der User-Edit-Lifecycle der Textarea unberührt bleibt. */}
          {isAnnahme && (
            <div
              data-testid={`vd-row-${index}-annahme-info`}
              className="mb-2 flex items-start gap-2 rounded-sm border border-slate-300 bg-slate-50/60 px-2 py-1.5 text-[11px] leading-snug text-slate-700"
              role="note"
            >
              <WarningCircle
                size={14}
                weight="fill"
                className="mt-0.5 shrink-0 text-slate-500"
                aria-hidden="true"
              />
              <span>
                <strong className="font-semibold">Anrechnungsfaktor zu 100 % angenommen</strong>
                {" — "}kein eindeutiger KV-Regel-Match. Bitte prüfen,
                ob die Tätigkeit als Angestellter oder Arbeiter eingestuft
                werden soll (z.B. Tätigkeitstext ergänzen, damit die
                KV-Regel greift).
              </span>
            </div>
          )}
          <Label htmlFor={`vd-${index}-begr`} className="text-[10px] mb-1">
            Begründung zur Anrechenbarkeit
          </Label>
          <textarea
            id={`vd-${index}-begr`}
            data-testid={`vd-row-${index}-begruendung`}
            /* Iter 20.36.f14.25 — Default-Höhe von 2 auf 6 Zeilen erhöht.
               Lange KV-Regel-Begründungen (WT-Kanzlei-Kombiregel,
               Altersfaktor + Höchstanrechnung, mehrere Sätze mit
               Sonderregel-Verweisen) waren mit rows=2 nur mit Scrollen
               oder manuellem Größenziehen lesbar. 6 Zeilen zeigen typische
               Begründungen komplett; `resize-y` bleibt für Extremfälle. */
            rows={6}
            value={row.anrechenbarkeit_begruendung}
            onChange={(e) =>
              onChange({ ...row, anrechenbarkeit_begruendung: e.target.value })
            }
            placeholder="z.B. Gleichartige Tätigkeit im kaufmännischen Bereich, voll anrechenbar."
            className="w-full text-sm rounded-sm border border-ink-300 px-2 py-1.5 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y"
          />
        </div>
      )}
      </div>
      )}
    </li>
  );
}

// Audit-Fix P2 — memoized: re-rendert nur bei tatsächlich geänderten Props.
export default React.memo(VordienstRow);
